import { computed, ref, watch, type Ref } from 'vue';
import type { Router } from 'vue-router';

import {
  DocumentSendAudienceType,
  DocumentSendFormat,
  documentSendApi,
  getDocumentSendFieldErrors,
  isDocumentSendUserFacingError,
} from '@/entities/DocumentSend';
import { hasProtheusRecipientEmail, protheusApi } from '@/entities/Protheus';
import type { ProtheusClient, ProtheusSupplier } from '@/entities/Protheus';
import { MAX_DOCUMENT_SEND_ATTACHMENTS } from '@/shared/config';

import {
  buildInvalidRecipientEmailsMessage,
  resolveInvalidRecipientEmailsMessage,
  sanitizeElectronicRecipientRow,
  validateElectronicRecipients,
  type ElectronicRecipientRow,
} from './electronicRecipient';
import {
  buildLocalUrlsPayload,
  countLocalFiles,
  normalizeLocalUrls,
  resolveRejectedUrlRows,
  TOO_MANY_ATTACHMENTS_MESSAGE,
  validateLocalUrls,
  URL_ROWS_REJECTED_MESSAGE,
  type LocalUrl,
} from './electronic-attachments';
import { toPhysicalRecipientOption } from './physicalRecipient';

const GENERIC_SEND_ERROR_MESSAGE =
  'Não foi possível realizar o envio. Verifique sua conexão e tente novamente.';
const PROTHEUS_WARNING_MESSAGE =
  'Não foi possível carregar os destinatários agora. Tente novamente em instantes.';

type ToastFn = (payload: {
  title: string;
  message: string;
  type: 'success' | 'error' | 'warning';
}) => void;

export function useElectronicSendForm(params: {
  gdDocumentRevisionIds: Ref<number[]>;
  isLoadingRecipientData: Ref<boolean>;
  isSubmitting: Ref<boolean>;
  localFiles: Ref<File[]>;
  localUrls: Ref<LocalUrl[]>;
  urlErrors: Ref<string[]>;
  message: Ref<string>;
  messageError: Ref<string>;
  recipientError: Ref<string>;
  router: Router;
  selectedAudienceType: Ref<DocumentSendAudienceType | ''>;
  selectedElectronicMode: Ref<'upload' | 'url' | ''>;
  selectedFormat: Ref<DocumentSendFormat | ''>;
  selectedPositionId: Ref<string>;
  sendDate: Ref<Date | null>;
  senderPositionError: Ref<string>;
  showErrorToast: ToastFn;
  showSuccessToast: ToastFn;
  showWarningToast: ToastFn;
  title: Ref<string>;
  titleError: Ref<string>;
  getSenderMemberId: () => number | null;
}) {
  const electronicClientRecipients = ref<ElectronicRecipientRow[]>([]);
  const electronicSupplierRecipients = ref<ElectronicRecipientRow[]>([]);
  const protheusClients = ref<ProtheusClient[]>([]);
  const protheusSuppliers = ref<ProtheusSupplier[]>([]);
  const electronicClientOptions = computed(() =>
    protheusClients.value.filter((client) => hasProtheusRecipientEmail(client, 'CLIENT'))
  );
  const electronicSupplierOptions = computed(() =>
    protheusSuppliers.value.filter((supplier) => hasProtheusRecipientEmail(supplier, 'SUPPLIER'))
  );

  const clearElectronicValidationErrors = (): void => {
    params.recipientError.value = '';
  };

  const resolveSubmitElectronicErrorMessage = (
    error: unknown,
    rejectedUrlRows: string[] | null,
    invalidRecipientEmailsMessage: string | null
  ) => {
    if (rejectedUrlRows !== null) {
      return URL_ROWS_REJECTED_MESSAGE;
    }

    if (invalidRecipientEmailsMessage !== null) {
      return invalidRecipientEmailsMessage;
    }

    if (isDocumentSendUserFacingError(error)) {
      return error.message;
    }

    return GENERIC_SEND_ERROR_MESSAGE;
  };

  const validateElectronicUrls = () => {
    if (params.selectedElectronicMode.value !== 'url') {
      return [];
    }

    const availableSlots =
      MAX_DOCUMENT_SEND_ATTACHMENTS -
      params.gdDocumentRevisionIds.value.length -
      countLocalFiles(params.selectedElectronicMode.value, params.localFiles.value);

    return validateLocalUrls(params.localUrls.value, availableSlots, TOO_MANY_ATTACHMENTS_MESSAGE);
  };
  const validateElectronicForm = () => {
    params.titleError.value = params.title.value.trim() === '' ? 'Título é obrigatório.' : '';
    params.messageError.value = params.message.value.trim() === '' ? 'Mensagem é obrigatória.' : '';
    params.senderPositionError.value =
      params.selectedPositionId.value === '' ? 'Cargo é obrigatório.' : '';
    params.recipientError.value = '';

    if (params.getSenderMemberId() === null) {
      params.senderPositionError.value = 'Selecione um cargo válido para o envio.';
    }

    const { recipients, recipientError, invalidEmailCount } = validateElectronicRecipients({
      audienceType: params.selectedAudienceType.value,
      clientRecipients: electronicClientRecipients.value,
      supplierRecipients: electronicSupplierRecipients.value,
      requireAudienceSelection: true,
    });

    params.recipientError.value = recipientError;

    params.urlErrors.value = validateElectronicUrls();

    return {
      isValid:
        params.titleError.value === '' &&
        params.messageError.value === '' &&
        params.senderPositionError.value === '' &&
        params.recipientError.value === '' &&
        params.urlErrors.value.every((urlError) => urlError === '') &&
        recipients.length > 0,
      recipients,
      invalidEmailCount,
    };
  };

  const clientOptions = computed(() => protheusClients.value.map(toPhysicalRecipientOption));
  const supplierOptions = computed(() => protheusSuppliers.value.map(toPhysicalRecipientOption));

  const setElectronicClientRecipients = (rows: ElectronicRecipientRow[]) => {
    electronicClientRecipients.value = rows.map(sanitizeElectronicRecipientRow);
    clearElectronicValidationErrors();
  };

  const setElectronicSupplierRecipients = (rows: ElectronicRecipientRow[]) => {
    electronicSupplierRecipients.value = rows.map(sanitizeElectronicRecipientRow);
    clearElectronicValidationErrors();
  };

  const setLocalFiles = (files: File[]) => {
    params.localFiles.value = files;
  };

  const setLocalUrls = (urls: LocalUrl[]) => {
    params.localUrls.value = urls;
  };

  const setUrlErrors = (errors: string[]) => {
    params.urlErrors.value = errors;
  };

  const submitElectronicSend = async () => {
    if (
      params.selectedFormat.value !== DocumentSendFormat.ELECTRONIC ||
      params.isSubmitting.value
    ) {
      return;
    }

    const validation = validateElectronicForm();

    if (!validation.isValid) {
      if (validation.invalidEmailCount > 0) {
        params.showErrorToast({
          title: 'Erro',
          message: buildInvalidRecipientEmailsMessage(validation.invalidEmailCount),
          type: 'error',
        });
      }

      return;
    }

    const senderMemberId = params.getSenderMemberId();

    if (senderMemberId === null) {
      params.senderPositionError.value = 'Selecione um cargo válido para o envio.';
      return;
    }

    params.isSubmitting.value = true;

    try {
      const metadata = {
        title: params.title.value.trim(),
        message: params.message.value.trim(),
        sendDate: params.sendDate.value?.toISOString() ?? new Date().toISOString(),
        senderMemberId,
        senderPositionId: Number(params.selectedPositionId.value),
        recipients: validation.recipients,
        gdDocumentRevisionIds: params.gdDocumentRevisionIds.value,
        localUrls: buildLocalUrlsPayload(
          params.selectedElectronicMode.value,
          params.localUrls.value
        ),
      };

      const formData = new FormData();
      formData.append('metadata', JSON.stringify(metadata));

      if (params.selectedElectronicMode.value !== 'url') {
        params.localFiles.value.forEach((file) => formData.append('files', file));
      }

      await documentSendApi.createElectronicDocumentSend(formData);

      params.showSuccessToast({
        title: 'Sucesso',
        message: 'Envio eletrônico realizado com sucesso.',
        type: 'success',
      });

      params.router.push('/gestao-documentos/controle-envio').catch(() => {});
    } catch (error) {
      const fieldErrors = getDocumentSendFieldErrors(error);
      const rejectedUrlRows = resolveRejectedUrlRows(params.localUrls.value, fieldErrors);
      const invalidRecipientEmailsMessage = resolveInvalidRecipientEmailsMessage(fieldErrors);

      if (rejectedUrlRows !== null) {
        params.urlErrors.value = rejectedUrlRows;
      }

      if (invalidRecipientEmailsMessage !== null) {
        params.recipientError.value = invalidRecipientEmailsMessage;
      }

      const errorMessage = resolveSubmitElectronicErrorMessage(
        error,
        rejectedUrlRows,
        invalidRecipientEmailsMessage
      );

      params.showErrorToast({
        title: 'Erro',
        message: errorMessage,
        type: 'error',
      });
    } finally {
      params.isSubmitting.value = false;
    }
  };

  const needsRecipientData = computed(
    () =>
      params.selectedFormat.value === DocumentSendFormat.ELECTRONIC ||
      params.selectedFormat.value === DocumentSendFormat.PHYSICAL ||
      params.selectedFormat.value === DocumentSendFormat.ELECTRONIC_PHYSICAL
  );

  watch(params.selectedFormat, (format) => {
    const includesElectronic =
      format === DocumentSendFormat.ELECTRONIC || format === DocumentSendFormat.ELECTRONIC_PHYSICAL;

    if (!includesElectronic) {
      params.selectedElectronicMode.value = '';
    } else if (!params.selectedElectronicMode.value) {
      params.selectedElectronicMode.value = 'upload';
    }
  });

  watch(params.selectedElectronicMode, (mode) => {
    if (mode === 'url') {
      params.urlErrors.value = validateElectronicUrls();
      return;
    }

    params.localUrls.value = normalizeLocalUrls(params.localUrls.value);
    params.urlErrors.value = [];
  });

  watch(params.selectedAudienceType, (audienceType) => {
    clearElectronicValidationErrors();

    if (audienceType === DocumentSendAudienceType.CLIENT) {
      electronicSupplierRecipients.value = [];
    }

    if (audienceType === DocumentSendAudienceType.SUPPLIER) {
      electronicClientRecipients.value = [];
    }
  });

  watch(
    needsRecipientData,
    async (shows) => {
      if (!shows || protheusClients.value.length > 0) {
        return;
      }

      params.isLoadingRecipientData.value = true;

      try {
        const [clients, suppliers] = await Promise.all([
          protheusApi.getClients(),
          protheusApi.getSuppliers(),
        ]);

        protheusClients.value = clients;
        protheusSuppliers.value = suppliers;
      } catch {
        params.showWarningToast({
          title: 'Aviso',
          message: PROTHEUS_WARNING_MESSAGE,
          type: 'warning',
        });
      } finally {
        params.isLoadingRecipientData.value = false;
      }
    },
    { immediate: true }
  );

  return {
    clientOptions,
    electronicClientOptions,
    electronicClientRecipients,
    electronicSupplierOptions,
    electronicSupplierRecipients,
    setElectronicClientRecipients,
    setElectronicSupplierRecipients,
    setLocalFiles,
    setLocalUrls,
    setUrlErrors,
    submitElectronicSend,
    supplierOptions,
    validateElectronicUrls,
  };
}
